import React from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AdminPanelSettings as AdminPanelIcon,
  Inventory as ProductsIcon,
  People as ClientsIcon,
  LocalShipping as ShippingIcon,
  Description as DocumentsIcon,
  Map as MapIcon,
  Assessment as ReportsIcon,
  // Chat as ChatIcon,
  ViewInAr as BundlesIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";

const drawerWidth = 240;

const baseMenuItems = [
  {
    key: "adminPanel",
    icon: <AdminPanelIcon />,
    path: "/admin-panel",
    adminOnly: true,
  },
  { key: "products", icon: <ProductsIcon />, path: "/products", adminOnly: false },
  { key: "warehouseMap", icon: <MapIcon />, path: "/warehouse-map", adminOnly: false },
  // { key: "chat", icon: <ChatIcon />, path: "/chat", adminOnly: false },
  { key: "clients", icon: <ClientsIcon />, path: "/clients", adminOnly: false },
  { key: "documents", icon: <DocumentsIcon />, path: "/documents", adminOnly: false },
  { key: "shipments", icon: <ShippingIcon />, path: "/shipments", adminOnly: false },
  { key: "bundles", icon: <BundlesIcon />, path: "/bundles", adminOnly: false },
  { key: "reports", icon: <ReportsIcon />, path: "/reports", adminOnly: false },
];

interface SideBarProps {
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
}

const SideBar: React.FC<SideBarProps> = ({
  mobileOpen,
  handleDrawerToggle,
}) => {
  const { t } = useTranslation();
  const { canAccessAdminPanel } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const menuItems = baseMenuItems.filter(
    (item) => !item.adminOnly || canAccessAdminPanel
  );

  const getTranslatedMenuItems = () => {
    return menuItems.map((item) => ({
      ...item,
      text: t(`navigation.${item.key}`),
    }));
  };

  const translatedMenuItems = getTranslatedMenuItems();

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={!isMobile || mobileOpen}
      onClose={handleDrawerToggle}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          bgcolor: isMobile ? "background.default" : "background.paper",
          color: "white",
          borderTopRightRadius: "20px",
          borderBottomRightRadius: "20px",
          border: "none",
          position: isMobile ? "fixed" : "fixed",
          height: "100vh",
          zIndex: theme.zIndex.drawer,
          overflowY: "auto",
        },
      }}
    >
      <List>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/"
            sx={{
              borderRadius: "12px",
              margin: "4px 16px",
              color: "white",
              "&:hover": {
                backgroundColor: "#2A2A2A",
              },
            }}
          >
            <ListItemIcon sx={{ color: "inherit", minWidth: "40px" }}>
              <img src={`${process.env.PUBLIC_URL || ""}/logo192.png`} alt="Boxly" style={{ width: 28, height: 28 }} />
            </ListItemIcon>

            <ListItemText
              primary="Boxly"
              primaryTypographyProps={{
                fontWeight: "700",
                fontSize: "1.1rem",
              }}
            />
          </ListItemButton>
        </ListItem>

        {translatedMenuItems.map((item) => (
          <ListItem key={item.key} disablePadding>
            <ListItemButton
              onClick={() => navigate(item.path)}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: "12px",
                margin: "4px 16px",
                "&.Mui-selected": {
                  backgroundColor: "#3A3A3A",
                  "&:hover": { backgroundColor: "#3A3A3A" },
                },
                "&:hover": { backgroundColor: "#2A2A2A" },
                "& .MuiListItemIcon-root, & .MuiListItemText-root": {
                  color: "white",
                },
              }}
            >
              <ListItemIcon sx={{ color: "inherit", minWidth: "40px" }}>
                {item.icon}
              </ListItemIcon>

              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontWeight: location.pathname === item.path ? "600" : "400",
                  fontSize: "0.9rem",
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
};

export default SideBar;
